/* ════════════ ALPINE NIGHT + PIXEL MARMOT ════════════
   The marmot page's counterpart to the home page starfield: a cold
   alpine night — sparse stars, blocky mountain ridges, drifting pixel
   snow — plus a 16×16 sentinel marmot that whistles on the hero HUD. */

import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ───────── atmosphere: stars, ridges, snow ───────── */
function alpine(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#alpine")!;
  const ctx = c.getContext("2d")!;

  type Star = { x: number; y: number; s: number };
  type Flake = { x: number; y: number; s: number; vy: number; sway: number; phase: number; col: string };
  type Ridge = { ys: number[]; col: string };

  const flakeColors = ["#e8eef4", "#cdd8e2", "#cdd8e2", "#ffb648"];
  let stars: Star[] = [];
  let flakes: Flake[] = [];
  let ridges: Ridge[] = [];
  const STEP = 10; // ridge column width — chunky on purpose

  const makeFlake = (y?: number): Flake => ({
    x: Math.random() * c.width,
    y: y ?? -4,
    s: Math.random() > 0.85 ? 2 : 1,
    vy: 0.2 + Math.random() * 0.5,
    sway: 0.3 + Math.random() * 1.0,
    phase: Math.random() * Math.PI * 2,
    col: flakeColors[(Math.random() * flakeColors.length) | 0]!,
  });

  // blocky random-walk ridge, y quantized so it reads as pixel art
  const makeRidge = (baseY: number, amp: number, col: string): Ridge => {
    const cols = Math.ceil(c.width / STEP) + 1;
    const ys: number[] = [];
    let y = baseY;
    for (let i = 0; i < cols; i++) {
      y += (Math.random() - 0.5) * amp;
      y = Math.max(baseY - amp * 2.4, Math.min(baseY + amp * 1.6, y));
      ys.push(Math.round(y / 4) * 4);
    }
    return { ys, col };
  };

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    stars = Array.from({ length: Math.min(80, Math.floor(c.width / 14)) }, () => ({
      x: Math.random() * c.width,
      y: Math.random() * c.height * 0.55,
      s: Math.random() > 0.9 ? 2 : 1,
    }));
    const count = Math.min(110, Math.floor((c.width * c.height) / 18000));
    flakes = Array.from({ length: count }, () => makeFlake(Math.random() * c.height));
    ridges = [
      makeRidge(c.height * 0.82, 9, "rgba(38, 24, 14, 0.85)"),
      makeRidge(c.height * 0.9, 12, "rgba(22, 13, 8, 0.95)"),
    ];
  };
  seed();
  addEventListener("resize", seed, { signal });

  const drawRidge = (r: Ridge) => {
    ctx.fillStyle = r.col;
    for (let i = 0; i < r.ys.length; i++) {
      const y = r.ys[i]!;
      ctx.fillRect(i * STEP, y, STEP, c.height - y);
      // snowcap: one bright pixel row on each crest
      ctx.fillStyle = "rgba(232, 238, 244, 0.18)";
      ctx.fillRect(i * STEP, y, STEP, 2);
      ctx.fillStyle = r.col;
    }
  };

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    for (const st of stars) {
      ctx.globalAlpha = 0.25 + Math.random() * 0.45;
      ctx.fillStyle = "#cdd8e2";
      ctx.fillRect(st.x | 0, st.y | 0, st.s, st.s);
    }
    ctx.globalAlpha = 1;
    for (const r of ridges) drawRidge(r);
    for (const f of flakes) {
      ctx.globalAlpha = 0.4 + 0.4 * Math.sin(f.phase);
      ctx.fillStyle = f.col;
      ctx.fillRect(f.x | 0, f.y | 0, f.s, f.s);
    }
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    draw();
    return;
  }
  let t = 0;
  const tick = () => {
    if (signal.aborted) return;
    t += 0.016;
    for (const f of flakes) {
      f.y += f.vy;
      f.x += Math.sin(t * 0.7 + f.phase) * f.sway * 0.25;
      f.phase += 0.01;
      if (f.y > c.height + 4) Object.assign(f, makeFlake());
      if (f.x < -4) f.x = c.width + 2;
      if (f.x > c.width + 4) f.x = -2;
    }
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

/* ───────── the sentinel marmot ───────── */
function marmot(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#marmot-px")!;
  const ctx = c.getContext("2d")!;
  const S = 16;
  const px = c.width / S;

  // d outline · b fur · B dark fur/tail · l belly+muzzle · n eyes/nose · t teeth
  const map = [
    "................",
    "...dd......dd...",
    "..dbd......dbd..",
    "...dbbbbbbbbd...",
    "..dbbbbbbbbbbd..",
    "..dbbnbbbbnbbd..",
    "..dbbllllllbbd..",
    "..dbbllnnllbbd..",
    "..dbbllttllbbd..",
    "..dbbbbbbbbbbd..",
    "..dbBllllllBbdB.",
    "..dbBllbbllBbdB.",
    "..dbBllllllBbdB.",
    "..dbbbbbbbbbbd..",
    "...dbbd..dbbd...",
    "................",
  ];
  const colors: Record<string, string> = {
    d: "#2a1708",
    b: "#c9803a",
    B: "#a05f24",
    l: "#f2c178",
    n: "#140902",
    t: "#fff3dc",
  };

  const drawSprite = () => {
    ctx.fillStyle = "#0e0805";
    ctx.fillRect(0, 0, c.width, c.height);
    for (let y = 0; y < S; y++)
      for (let x = 0; x < S; x++) {
        const ch = map[y]?.[x] ?? ".";
        if (ch === ".") continue;
        ctx.fillStyle = colors[ch] ?? "#ffb648";
        ctx.fillRect(x * px, y * px, px, px);
      }
  };

  drawSprite();
  if (REDUCED) return;

  // every few seconds the sentinel whistles: tiny pixel eighth-notes
  // bounce out of the muzzle and float up past the ear
  const WHISTLE_MS = 1600;
  const PAUSE_MS = 3000;
  const hp = px / 2; // notes are drawn at half-cell resolution, daintier
  const NOTE = [
    ".11",
    ".1.",
    ".1.",
    "11.",
  ];
  const drawNote = (gx: number, gy: number, col: string) => {
    // gx/gy in half-cell units, quantized so the note stays crisp
    const ox = Math.round(gx) * hp;
    const oy = Math.round(gy) * hp;
    ctx.fillStyle = col;
    for (let y = 0; y < NOTE.length; y++)
      for (let x = 0; x < 3; x++)
        if (NOTE[y]![x] === "1") ctx.fillRect(ox + x * hp, oy + y * hp, hp, hp);
  };

  let start = performance.now() + 1500;
  const tick = (now: number) => {
    if (signal.aborted) return;
    const t = now - start;
    if (t > WHISTLE_MS + PAUSE_MS) start = now;
    drawSprite();
    if (t > 0 && t < WHISTLE_MS) {
      // two notes, staggered; each rises from the mouth (≈ cell 9.5, 7)
      // toward the upper-right corner with a little sway
      for (let i = 0; i < 2; i++) {
        const p = t / WHISTLE_MS - i * 0.35;
        if (p <= 0 || p > 0.65) continue;
        const q = p / 0.65;
        const gx = 19 + q * 9 + Math.sin(q * Math.PI * 2 + i * 2.5) * 1.2;
        const gy = 15 - q * 11;
        ctx.globalAlpha = q < 0.15 ? q / 0.15 : 1 - Math.max(0, (q - 0.6) / 0.4);
        drawNote(gx, gy, i % 2 ? "#fff3dc" : "#ffb648");
      }
      ctx.globalAlpha = 1;
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

onPage((signal) => {
  alpine(signal);
  marmot(signal);
});
